import React, { useState, useEffect, useRef, useCallback } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import FilterOptionsMenu from "../../commons/FilterOptionsMenu/FilterOptionsMenu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import TablePagination from "../../commons/Pagination/TablePagination";
import { Button } from "react-bootstrap";
import { FaSyncAlt } from "react-icons/fa";
import {
  UI_CONSTANTS,
  ORDER_STATUS_FILTER_OPTIONS,
  getOrderStatusLabel,
  getOrderStatusCodeFromLabel,
  getDriverTypeLabel,
  getOrderTypeLabel,
} from "../../utils/constants";
import { applyListState } from "../../utils/listNavigation";

function OrderManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [filter, setFilter] = useState("All");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef(null);
  const restoredRef = useRef(false);


  // Helper function to get payment method text
  const getPaymentMethod = (method) => {
    return method === "ONLINE" ? "Online" : method === "COD" ? "Cash on Delivery" : method;
  };

  const getOrderList = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const trimmedSearch = search.trim();
      const params = new URLSearchParams({
        page,
        limit,
        ...(trimmedSearch && { search: trimmedSearch }),
      });

      // Add order_status filter if not "All"
      if (filter !== "All") {
        const statusCode = getOrderStatusCodeFromLabel(filter);
        if (statusCode !== undefined) {
          params.append("order_status", statusCode);
        }
      }

      const response = await handleApiCall("get", `${endpoints.orderList}?${params}`);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const responseData = response?.data?.data || {};
        const ordersData = responseData.orders || responseData.data || [];
        setOrders(ordersData);
        setTotalPages(responseData.pagination?.totalPages || 1);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError(true);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filter]);

  useEffect(() => {
    getOrderList();
  }, [getOrderList]);

  useEffect(() => {
    const listState = location.state?.listState;
    if (!listState || restoredRef.current) return;
    restoredRef.current = true;
    applyListState(listState, { setPage, setSearch, setFilter });
  }, [location.state]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const orderListState = { page, search, filter };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Order Management</h3>
            </div>
            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0">
                  <form className="col-md-4 px-0">
                    <div className="form-group my-0">
                      <input
                        className="form-control search-icon left-icon border"
                        placeholder={UI_CONSTANTS.PLACEHOLDERS.ORDER_SEARCH}
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                  </form>
                  <FilterTrigger
                    ref={filterRef}
                    active={filter !== "All"}
                    className="ms-auto"
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    {showFilterDropdown && (
                      <FilterOptionsMenu
                        options={ORDER_STATUS_FILTER_OPTIONS}
                        value={filter}
                        style={{ right: "calc(100% + 10px)", top: 0 }}
                        onSelect={(option) => {
                          setFilter(option);
                          setShowFilterDropdown(false);
                          setPage(1);
                        }}
                      />
                    )}
                  </FilterTrigger>
                  <Button
                    type="button"
                    className="rounded-lg ml-3 d-flex align-items-center justify-content-center"
                    onClick={() => getOrderList()}
                    disabled={loading}
                    title="Reload"
                    aria-label="Reload"
                  >
                    <FaSyncAlt />
                  </Button>
                </div>
              </div>
              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th width="80">S.No.</th>
                        <th width="150">Order id</th>
                        <th>Order Type</th>
                        <th>Customer Name</th>
                        <th>Restaurant Name</th>
                        <th>Assigned Driver</th>
                        <th>Driver Type</th>
                        <th>Order status</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">{UI_CONSTANTS.BUTTON_LABELS.LOADING}</span>
                            </div>
                          </td>
                        </tr>
                      ) : orders.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      ) : (
                        orders.map((order, index) => (
                          <tr key={order._id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td className="text-center">#{order.order_number || "N/A"}</td>
                            <td>{getOrderTypeLabel(order.order_type)}</td>
                            <td>{order.customer_id?.full_name || "N/A"}</td>
                            <td>{order.restaurant_id?.restaurant_name || "N/A"}</td>
                            <td>{order.assigned_driver_id?.full_name || "N/A"}</td>
                            <td>{getDriverTypeLabel(order.assigned_driver_type)}</td>
                            <td>{getOrderStatusLabel(order.order_status)}</td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <Link
                                  to={`/view-order/${order._id}`}
                                  state={{
                                    from: "/order-management",
                                    listState: orderListState,
                                  }}
                                  className="mx-2"
                                >
                                  <img src={viewImage} alt="View" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderManagement;
